import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import InternalAssignment from '../../../../Model/InternalAssignment';
import { AssignmentRole } from '../../../../utils/internalAssignments';
import useComponentMounted from '../../../../hooks/useComponentMounted';

/*
  Stale POOL rows — a pooled coach who no longer resolves to a coach on the list — are deliberately
  not reported here. CoachesListContextProvider queries every current assignment across all roles,
  POOL included, and already reports exactly that condition for any coach missing from the external
  coach list. It is mounted on this page and is where coachesList itself comes from, so reporting
  again would double every Sentry event for the same row.
*/
const usePoolRoster = (isEnabled = true) => {
  const [poolAssignmentsCol, setPoolAssignmentsCol] = useState({ docs: [] });
  const [acAssignmentsCol, setAcAssignmentsCol] = useState({ docs: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const isComponentMountedRef = useComponentMounted();
  // Both queries are org-wide, so a tab flip away and back must reuse the first successful load
  // rather than refetching every current AC assignment again.
  const hasLoadedRef = useRef(false);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    const init = async () => {
      hasLoadedRef.current = true;
      setIsLoading(true);
      setLoadError(null);
      try {
        const [poolCol, acCol] = await Promise.all([
          InternalAssignment.getAllCurrentAssignmentsByRole(AssignmentRole.POOL),
          InternalAssignment.getAllCurrentAssignmentsByRole(AssignmentRole.ASSISTANT_COACH),
        ]);

        if (isComponentMountedRef.current) {
          setPoolAssignmentsCol(poolCol);
          setAcAssignmentsCol(acCol);
        }
      } catch (error) {
        hasLoadedRef.current = false;
        if (isComponentMountedRef.current) {
          setLoadError(error);
        }
      } finally {
        if (isComponentMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    if (isEnabled && !hasLoadedRef.current) {
      init();
    }
  }, [isComponentMountedRef, isEnabled, reloadCount]);

  /*
    A failed load leaves every pool control disabled with no way back: the effect only refires when
    the tab flips, which is neither discoverable nor something an operator should have to know.
  */
  const reload = useCallback(() => {
    hasLoadedRef.current = false;
    setReloadCount((count) => count + 1);
  }, []);

  return useMemo(() => ({
    poolAssignmentsCol,
    acAssignmentsCol,
    isLoading,
    loadError,
    reload,
  }), [
    poolAssignmentsCol,
    acAssignmentsCol,
    isLoading,
    loadError,
    reload,
  ]);
};

export default usePoolRoster;
